import {Box, Link as ChakraLink, HStack, Text, VStack} from '@chakra-ui/layout';
import {CadencierTags} from './components/CadencierTags';
import {HaliRenderOne, useHalState, useHalStates} from '@synako/haligator';
import {useCadencier, useClient} from '@/haligator-factory';
import {useEffect, useMemo, useState, useTransition} from 'react';
import {Link} from 'react-router-dom';
import {
  routes,
  useLivraisonsPossiblesContext,
  useSearchClient,
} from 'ff-common';
import {HEADER_HEIGHT} from '@/constants/layout';

import {ArrowForwardIcon} from '@chakra-ui/icons';
import {useTranslation} from 'react-i18next';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {usePanier} from 'ff-common';
import {BlueButton} from '@/components/Button/BlueButton';
import {Skeleton} from '@chakra-ui/react';
import {CadencierSection} from './controllers/CadencierSection';
import {
  ArticleStatutEvolutionEnum,
  Cadencier,
  HitItemCatalogue,
  TriSelection,
} from 'api-types/ffconnect';
import React from 'react';
import {FrozenToggle} from './components/FrozenToggle';
import {State} from '@synako/halitrek';
import {
  ArticlesCadencierSuggestionsAjoutesEventDesc,
  SuggestionsStateDesc,
} from 'api-types/ffconnect-hal';
import {FilterBar} from '@/components/FilterBar/FilterBar';
import {
  SortType,
  TriArticlesCadencier,
} from '../ligneProduit/TriArticlesCadencier';
import {TPath, useTypedQuery} from '@synako/typed-path';
import {FiltresItemCatalogue, coerceFiltresItemCatalogue} from 'ff-common';
import {Results} from '@orama/orama';
import {groupBy, sortBy} from 'lodash-es';
import {DotLoaderSlow} from '@/components/Loading/DotLoaderSlow';
import {usePeutVoirEvolutionArticle} from '@/hooks/auth/usePermissionsCommerciales';
import {CadencierTendancesFiltersBar} from './components/CadencierTendancesFiltersBar';
import {CadencierSearchInput} from './components/CadencierSearchInput';
import {qCadencierPourRecherche} from './cadencier-recherche';
import {
  TENDANCES_ORDER,
  buildFilteredItemResults,
  buildStatutByCode,
  compterTendancesSurHits,
  emptyTendancesCounts,
  filtrerHitsParTendances,
} from './cadencier-tendances-utils';

/** Filtres Cadencier = filtres Orama catalogue + tendances (hors Orama, validés côté page). */
type CadencierFilters = FiltresItemCatalogue & {
  tendances?: string[];
};

const coerceCadencierFilters = {
  ...coerceFiltresItemCatalogue,
  tendances: 'string[]',
} as const;

const STATUTS_VALIDES = new Set<string>(TENDANCES_ORDER);

const isStatutEvolution = (
  value: string,
): value is ArticleStatutEvolutionEnum => STATUTS_VALIDES.has(value);

export const MonCadencier: React.FC = () => {
  const cadencierNav = useCadencier();
  return (
    <HaliRenderOne
      nav={cadencierNav}
      skeleton={() => (
        <Skeleton
          width='100%'
          height='10'
          m='5'
        />
      )}
      render={({data: cadencier}) => (
        <MonCadencierContent cadencier={cadencier} />
      )}
    />
  );
};

const MonCadencierContent: React.FC<{cadencier: Cadencier}> = ({cadencier}) => {
  const {t} = useTranslation();
  const {effectiveTemperature} = useLivraisonsPossiblesContext();

  // Pour gérer le tri des articles dans chaque section
  const clientNav = useClient();
  const {state: suggestionsState} = useHalState(
    clientNav.follow('suggestions'),
  );

  // Tendances de ligne : visibles uniquement avec la permission Vue Commerce.
  const peutVoirEvolution = usePeutVoirEvolutionArticle();
  const {state: clientState} = useHalState(clientNav);
  const tendancesNav =
    peutVoirEvolution && clientState?.hasOneLink('cadencier_tendances')
      ? clientNav.follow('cadencier_tendances')
      : undefined;
  const {state: tendancesState} = useHalState(tendancesNav);

  // Event concernant l'ajout au panier des articles ayant une suggestion de quantité
  // Permet de remonter l'event pour l'affichage du picto
  const [lastSuggestionEvent, setLastSuggestionEvent] = useState<
    State<ArticlesCadencierSuggestionsAjoutesEventDesc> | undefined
  >();
  const handleSuggestionSuccess = (
    event: State<ArticlesCadencierSuggestionsAjoutesEventDesc> | null,
  ) => {
    setLastSuggestionEvent(event ?? undefined);
  };

  // Etat du type de tri des articles
  // Persistance dans le localStorage
  const [sortType, setSortType] = useState<SortType>(() => {
    const saved = localStorage.getItem('cadencier-sort-type');
    return saved ? (saved as SortType) : 'alphabetic';
  });

  useEffect(() => {
    localStorage.setItem('cadencier-sort-type', sortType);
  }, [sortType]);

  // Filtres persistés dans l'URL (Orama + tendances multi-select)
  const filtersHook = useTypedQuery<TPath<{Query: CadencierFilters}>>(
    coerceCadencierFilters,
  );
  const selectedTendances = useMemo(
    () => (filtersHook.params.tendances ?? []).filter(isStatutEvolution),
    [filtersHook.params.tendances],
  );

  // Pré-filtrage automatique par température quand la tournée est mono-température
  // (surgelé uniquement ou frais uniquement). L'utilisateur peut désactiver via le FrozenToggle.
  const [hasInitTemperature, setHasInitTemperature] = useState(false);
  useEffect(() => {
    if (hasInitTemperature) return;
    if (
      effectiveTemperature !== 'les_deux' &&
      filtersHook.params.temperature_livraison === undefined
    ) {
      filtersHook.change('temperature_livraison', effectiveTemperature);
    }
    setHasInitTemperature(true);
  }, [effectiveTemperature, filtersHook, hasInitTemperature]);

  const temperatureFilterActive =
    filtersHook.params.temperature_livraison !== undefined;

  // Recherche Orama avec filtre est_cadencier: true
  const {searchItems, isBuilding, indexIsBuilt, indexVersion} =
    useSearchClient();
  const [itemResults, setItemResults] = useState<Results<HitItemCatalogue>>();
  // On utilise une transition pour le user experience lors du chargement des résultats de recherche
  const [isPending, startTransition] = useTransition();

  // Construire les filtres Orama depuis les paramètres URL
  // `q` : recherche texte (libellé / marque) — même moteur que le catalogue (#3234).
  // Sous SEARCH_MIN_CHARS, le terme reste dans l'URL mais n'est pas envoyé à Orama.
  const oramaFilters: FiltresItemCatalogue = useMemo(
    () => ({
      est_cadencier: true,
      q: qCadencierPourRecherche(filtersHook.params.q),
      nature_du_prix: filtersHook.params.nature_du_prix,
      labels: filtersHook.params.labels,
      famille_fromage: filtersHook.params.famille_fromage,
      type_de_lait: filtersHook.params.type_de_lait,
      traitement_thermique_du_lait:
        filtersHook.params.traitement_thermique_du_lait,
      temperature_livraison: filtersHook.params.temperature_livraison,
      sortBy: 'alphabetique',
    }),
    [filtersHook.params],
  );

  useEffect(() => {
    if (!isBuilding && indexIsBuilt) {
      // Pas de pagination, on veut tous les articles du cadencier
      searchItems(oramaFilters, 10000).then((results) => {
        startTransition(() => {
          setItemResults(results);
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oramaFilters, isBuilding, indexIsBuilt, indexVersion]);

  const statutByCode = useMemo(
    () => buildStatutByCode(tendancesState?.data.articles),
    [tendancesState?.data.articles],
  );

  // Compteurs des pastilles : hits Orama (autres filtres) × map tendances.
  // Tant que les tendances ne sont pas chargées, on n'expose que le total Orama
  // (évite un flash où tout tombe en « a-gagner » faute de map).
  const tendancesCounts = useMemo(() => {
    if (!itemResults) return emptyTendancesCounts();
    if (!tendancesState) {
      return {...emptyTendancesCounts(), tout: itemResults.hits.length};
    }
    return compterTendancesSurHits(itemResults.hits, statutByCode);
  }, [itemResults, tendancesState, statutByCode]);

  // Hits affichés = Orama ∩ tendances sélectionnées ; facettes recalculées pour FilterBar
  const displayItemResults = useMemo(() => {
    if (!itemResults) return undefined;
    if (
      !peutVoirEvolution ||
      selectedTendances.length === 0 ||
      !tendancesState
    ) {
      return itemResults;
    }
    const filteredHits = filtrerHitsParTendances(
      itemResults.hits,
      selectedTendances,
      statutByCode,
    );
    return buildFilteredItemResults(itemResults, filteredHits);
  }, [
    itemResults,
    peutVoirEvolution,
    selectedTendances,
    statutByCode,
    tendancesState,
  ]);
  // Sections filtrées : regroupement des hits Orama par première cat_web,
  // enrichies avec le libellé web venant de cadencier.tri_selection.
  // Utilisées à la fois pour les tags (nav) et le corps (rendu des sections).
  // Mémoïsé pour éviter de recréer les tableaux à chaque render — sinon
  // CadencierTags détacherait/réattacherait ses listeners de scroll inutilement.
  const filteredSections = useMemo(
    () =>
      displayItemResults?.hits
        ? construireSectionsFiltrees(
            displayItemResults,
            cadencier.tri_selection,
          )
        : undefined,
    [displayItemResults, cadencier.tri_selection],
  );
  const filteredCategories = useMemo(
    () =>
      filteredSections ??
      cadencier.tri_selection.map((c) => ({
        categorie_id: c.categorie_id,
        libelle_web: c.libelle_web,
      })),
    [filteredSections, cadencier.tri_selection],
  );

  const hasNoFilteredResults =
    !!displayItemResults && displayItemResults.hits.length === 0;

  const resetAllFilters = (preservePromo = false) => {
    filtersHook.changeMultiple({
      q: undefined,
      nature_du_prix: preservePromo ? 'promo' : undefined,
      labels: undefined,
      famille_fromage: undefined,
      type_de_lait: undefined,
      traitement_thermique_du_lait: undefined,
      temperature_livraison: undefined,
      tendances: undefined,
    });
  };

  const emptyTendanceLabel = selectedTendances
    .map((statut) => t(`cadencier.filtres_tendance.${statut}`))
    .join(', ');

  return (
    <Box
      bg='white'
      py={{base: '0', lg: '3'}}
    >
      <VStack
        bg={{lg: 'white'}}
        pt='20px'
        gap={{base: '0', lg: '2'}}
        alignItems='start'
        position='relative'
      >
        <Box
          id='cadencier_header_container'
          width='100%'
          zIndex='dropdown'
          position={{md: 'sticky'}}
          top={{md: HEADER_HEIGHT.md}}
          bg='white'
        >
          {/* Pastilles tendances : sous les onglets Mes Produits, au-dessus des catégories */}
          {peutVoirEvolution && (
            <Box px={{base: '2', md: '4', lg: '6'}}>
              <CadencierTendancesFiltersBar
                counts={tendancesCounts}
                selected={selectedTendances}
                onChange={(next) => {
                  filtersHook.change(
                    'tendances',
                    next.length > 0 ? next : undefined,
                  );
                }}
              />
            </Box>
          )}
          {/* Tags de navigation par catégorie — filtrés selon les résultats Orama */}
          <CadencierTags
            categories={filteredCategories}
            containerId='cadencier_body_container'
            headerContainerId='cadencier_header_container'
            useDrawerScrolling={false}
          />
          {effectiveTemperature !== 'les_deux' && (
            <Box px={{base: '2', md: '4', lg: '6'}}>
              <FrozenToggle
                temperature_de_livraison={effectiveTemperature}
                isChecked={temperatureFilterActive}
                onChange={() => {
                  filtersHook.change(
                    'temperature_livraison',
                    temperatureFilterActive ? undefined : effectiveTemperature,
                  );
                }}
              />
            </Box>
          )}
        </Box>
        {/* Barre de filtres avec facettes Orama (recalculées si filtre tendance actif) */}
        <FilterBar
          itemResults={displayItemResults}
          filters={filtersHook.params}
          onFilterChange={(key, value) => {
            filtersHook.change(key, value as never);
          }}
          onRemoveAllFilters={resetAllFilters}
          showTriCadencier
          showSuggestionActions
          sortType={sortType}
          onSortTypeChange={setSortType}
          onSuggestionSuccess={handleSuggestionSuccess}
          containerProps={{px: {base: '2', md: '4', lg: '6'}}}
          leadingContent={({variant, onExpandedChange}) => (
            <CadencierSearchInput
              filtersHook={filtersHook}
              variant={variant}
              onExpandedChange={onExpandedChange}
            />
          )}
        />
        {isBuilding || !displayItemResults ? (
          <HStack
            w='100%'
            justifyContent='center'
          >
            <DotLoaderSlow />
          </HStack>
        ) : hasNoFilteredResults ? (
          <VStack
            w='100%'
            py='10'
            spacing='3'
            px={{base: '2', md: '4', lg: '6'}}
          >
            <Text
              color='gray.dark'
              fontWeight='500'
              textAlign='center'
            >
              {selectedTendances.length > 0
                ? t('cadencier.aucune_ligne_tendance', {
                    tendances: emptyTendanceLabel,
                  })
                : t('cadencier.aucun_article_filtre')}
            </Text>
            <ChakraLink
              as='button'
              type='button'
              color='blue.text'
              fontWeight='600'
              textDecoration='underline'
              onClick={() => {
                resetAllFilters();
              }}
            >
              {t('global.reset_filters')}
            </ChakraLink>
          </VStack>
        ) : (
          <Box
            px={{base: '2', md: '4', lg: '6'}}
            w='100%'
          >
            <Box
              id='cadencier_body_container'
              width='100%'
              border='1px solid'
              borderColor='gray.light'
              borderRadius='12px'
              boxShadow='md'
              opacity={isPending ? 0.6 : 1}
              transition='opacity 0.15s'
            >
              <CadencierBody
                sections={filteredSections ?? []}
                // gestion des events concernant l'ajout des articles avec suggestions de quantité au panier
                lastSuggestionEvent={lastSuggestionEvent}
                // pour tri des articles dans chaque section
                sortType={sortType}
                suggestionsState={suggestionsState}
              />
            </Box>
          </Box>
        )}
        <ActiveGoToPanier />
      </VStack>
    </Box>
  );
};

const ActiveGoToPanier = () => {
  const {t} = useTranslation('common');
  const {buildClientRoute} = useClientRoute();
  const {state: panier} = useHalState(usePanier());
  return (
    <Box
      m='5'
      ml='auto'
    >
      {panier && (
        <Link
          to={buildClientRoute(routes.client.panier, {
            date_panier_actif: panier.data.livraison_tolede.date_de_livraison,
          })}
        >
          <BlueButton
            texte={t('cadencier.cta_panier')}
            rightIcon={<ArrowForwardIcon />}
            p='7'
            m='2'
          />
        </Link>
      )}
    </Box>
  );
};

// Articles volontairement tronqués à `{code}` : ni `temperature_livraison`
// ni `dans_catalogue` ne sont remplis depuis les hits Orama.
type SectionFiltree = Omit<TriSelection[number], 'articles'> & {
  articles: {code: string}[];
};

/**
 * Regroupe les hits Orama par première cat_web et enrichit depuis tri_selection.
 * Les sections sans correspondance dans tri_selection sont filtrées.
 * Les tags et le cadencierBody partagent exactement la même source de vérité.
 */
const construireSectionsFiltrees = (
  itemResults: Results<HitItemCatalogue>,
  categoriesRef: TriSelection,
): SectionFiltree[] => {
  // Index des libellés de catégories depuis tri_selection
  const catIndex = new Map(categoriesRef.map((c) => [c.categorie_id, c]));

  // Grouper par première catégorie web
  const grouped = groupBy(itemResults.hits, (hit) => hit.document.cat_web[0]);
  return sortBy(
    Object.entries(grouped)
      .filter(([catId]) => catId && catIndex.has(catId))
      .map(([catId, hits]) => {
        const ref = catIndex.get(catId)!;
        return {
          categorie_id: catId,
          libelle_web: ref.libelle_web,
          libelle_court: ref.libelle_court,
          articles: hits.map((hit) => ({code: hit.document.code})),
        };
      }),
    (s) => parseInt(s.categorie_id, 10),
  );
};

const CadencierBody: React.FC<{
  sections: SectionFiltree[];
  lastSuggestionEvent?: State<ArticlesCadencierSuggestionsAjoutesEventDesc>;
  sortType: SortType;
  suggestionsState?: State<SuggestionsStateDesc>;
}> = ({sections, lastSuggestionEvent, sortType, suggestionsState}) => {
  const lignesHook = useHalStates(usePanier().followAll('lignes_de_panier'));

  return sections.map(({categorie_id, libelle_web, articles}) => {
    // Tri des articles selon le critère choisi
    const sortedArticles = TriArticlesCadencier(
      articles,
      sortType,
      suggestionsState,
    );

    return (
      <CadencierSection
        id={categorie_id}
        key={categorie_id}
        title={libelle_web}
        // articles triés selon le critère choisi
        articles={sortedArticles}
        lignesDePanier={lignesHook.states ?? []}
        lastSuggestionEvent={lastSuggestionEvent}
      />
    );
  });
};
